// SynthesisService.js - Version unifiée simplifiée SANS paramètre de contexte
// ✅ WebSearchSpace ET InspirationModule utilisent LES MÊMES fonctions
// ✅ AUCUN paramètre de contexte - c'est le contenu qui détermine le comportement
// ✅ Une seule méthode searchSources() pour tout le monde
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  deleteDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { ClientLanguageDetector } from '../components/LanguageService.js';

// ✅ Import pour la recherche unifiée via backend
import sourceService from '../CP-Sourcing/SourceService';

class SynthesisService {
  
  // ============================================================================
  // MÉTHODES EXISTANTES INCHANGÉES - Classification et enrichissement
  // ============================================================================
  
  classifySourceCategory(source) {
    const url = source.url || source.usableUrl || '';
    const domain = (source.domain || '').toLowerCase();
    const title = (source.title || '').toLowerCase();
    const description = (source.description || '').toLowerCase();
    
    const governmentalDomains = [
      '.gov', '.gouv.', '.govt.', '.gob.', '.gov.', '.mil',
      'europa.eu', 'ec.europa.eu', 'eur-lex.europa.eu',
      'who.int', 'un.org', 'unesco.org', 'worldbank.org'
    ];
    
    const educationalDomains = [
      '.edu', '.ac.', '.univ-', 'university', 'college',
      'harvard.edu', 'mit.edu', 'stanford.edu', 'oxford.ac.uk',
      'cambridge.org', 'springer.com', 'elsevier.com',
      'arxiv.org', 'researchgate.net', 'scholar.google',
      'coursera.org', 'edx.org', 'khanacademy.org'
    ];
    
    const mediaDomains = [
      'bbc.', 'cnn.', 'reuters.', 'ap.org', 'npr.org',
      'lemonde.fr', 'figaro.fr', 'liberation.fr',
      'nytimes.com', 'washingtonpost.com', 'guardian.co.uk',
      'france24.com', 'rfi.fr', 'tf1.fr', 'francetvinfo.fr'
    ];
    
    const nonprofitDomains = [
      '.org', 'wikipedia.org', 'mozilla.org', 'greenpeace.org',
      'redcross.org', 'amnesty.org', 'oxfam.org', 'unicef.org'
    ];
    
    for (const govDomain of governmentalDomains) {
      if (domain.includes(govDomain)) return 'Governmental';
    }
    for (const eduDomain of educationalDomains) {
      if (domain.includes(eduDomain)) return 'Educational';
    }
    for (const mediaDomain of mediaDomains) {
      if (domain.includes(mediaDomain)) return 'Media';
    }
    for (const nonprofitDomain of nonprofitDomains) {
      if (domain.includes(nonprofitDomain)) return 'Non-profit';
    }
    
    const categoryKeywords = {
      'Governmental': ['government', 'ministry', 'department', 'agency', 'official', 'gouvernement', 'ministère', 'administration', 'officiel', 'policy', 'regulation', 'law', 'legislation'],
      'Educational': ['university', 'research', 'study', 'academic', 'scholar', 'université', 'recherche', 'étude', 'académique', 'journal', 'thesis', 'paper', 'education'],
      'Media': ['news', 'press', 'media', 'journalist', 'reporter', 'actualités', 'presse', 'journaliste', 'information', 'breaking', 'headline', 'article'],
      'Non-profit': ['foundation', 'charity', 'nonprofit', 'organization', 'fondation', 'association', 'ong', 'organisation', 'volunteer', 'donation', 'cause']
    };
    
    const contentText = `${title} ${description}`;
    for (const [category, keywords] of Object.entries(categoryKeywords)) {
      for (const keyword of keywords) {
        if (contentText.includes(keyword)) {
          return category;
        }
      }
    }
    
    return 'Commercial';
  }

  classifyContentType(source) {
    const url = source.url || source.usableUrl || '';
    const title = (source.title || '').toLowerCase();
    const description = (source.description || '').toLowerCase();
    const domain = source.domain || '';
    
    const fileExtensionTypes = {
      'pdf': 'document', 'doc': 'document', 'docx': 'document', 'xls': 'document', 'xlsx': 'document',
      'ppt': 'document', 'pptx': 'document', 'txt': 'document', 'rtf': 'document', 'odt': 'document',
      'ods': 'document', 'odp': 'document'
    };
    
    for (const [extension, type] of Object.entries(fileExtensionTypes)) {
      if (url.toLowerCase().includes(`.${extension}`)) {
        return type;
      }
    }
    
    if (domain.includes('youtube.com') || domain.includes('youtu.be') ||
        domain.includes('vimeo.com') || domain.includes('dailymotion.com') ||
        domain.includes('twitch.tv') || domain.includes('video')) {
      return 'video';
    }
    
    const videoKeywords = ['video', 'vidéo', 'watch', 'streaming', 'player'];
    for (const keyword of videoKeywords) {
      if (title.includes(keyword) || description.includes(keyword) || url.includes(keyword)) {
        return 'video';
      }
    }
    
    return 'website';
  }
  
  async enrichSourceMetadata(source) {
    try {
      const textContent = `${source.title || ''} ${source.description || ''} ${source.snippet || ''}`.trim();
      const detectedLanguage = textContent ? 
        await ClientLanguageDetector.detectLanguage(textContent) : 'unknown';
      
      const sourceCategory = this.classifySourceCategory(source);
      const contentType = this.classifyContentType(source);
      
      return {
        ...source,
        language: detectedLanguage,
        sourceCategory: sourceCategory,
        contentType: contentType,
        enrichedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Erreur lors de l\'enrichissement des métadonnées:', error);
      return {
        ...source,
        language: 'unknown',
        sourceCategory: 'Commercial',
        contentType: 'website',
        enrichedAt: new Date().toISOString()
      };
    }
  }
  
  async enrichSourcesBatch(sources, batchSize = 5) {
    const enrichedSources = [];
    
    for (let i = 0; i < sources.length; i += batchSize) {
      const batch = sources.slice(i, i + batchSize);
      console.log(`📊 Enrichissement lot ${Math.floor(i/batchSize) + 1}/${Math.ceil(sources.length/batchSize)} (${batch.length} sources)`);
      
      const batchPromises = batch.map(source => this.enrichSourceMetadata(source));
      
      try {
        const batchResults = await Promise.all(batchPromises);
        enrichedSources.push(...batchResults);
      } catch (error) {
        console.error(`❌ Erreur lors du traitement du lot ${i}-${i + batchSize}:`, error);
        enrichedSources.push(...batch.map(source => ({
          ...source,
          language: 'unknown',
          sourceCategory: 'Commercial',
          contentType: 'website',
          enrichedAt: new Date().toISOString()
        })));
      }
      
      if (i + batchSize < sources.length) {
        console.log('⏳ Pause entre lots (2s)...');
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    
    return enrichedSources;
  }

  // ============================================================================
  // ✅ NOUVELLE MÉTHODE UNIFIÉE : Recherche de sources
  // Les deux modules utilisent EXACTEMENT la même fonction
  // ============================================================================
  
  /**
   * ✅ RECHERCHE UNIFIÉE - Une seule méthode pour WebSearchSpace ET InspirationModule
   * Garantit automatiquement la mise à jour de la collection "recherches"
   * 
   * @param {Object} searchParams - Paramètres de recherche
   * @param {string} token - Token d'authentification
   * @returns {Promise<Object>} - Résultat de la recherche
   */
  async searchSources(searchParams, token) {
    const {
      userId,
      competenceId,
      userObjective,
      query = '',
      languages = ['en'],
      country = '',
      maxSources = 20,
      selectedProviders = [],
      preferredTypes = null,
      useAI = true,
      enableGeographicLocalization = true,
      localGlobalRatio = 0.7,
      userPreferences,
      semanticAnalysis,
      geographicAnalysis
    } = searchParams;

    try {
      console.log('🔄 SynthesisService - Recherche unifiée via backend');
      
      // ✅ APPEL UNIFIÉ : Toujours passer par le backend pour cohérence
      const searchRequest = {
        userId,
        competenceId,
        userObjective,
        query, // ✅ Pas de modification automatique - c'est à l'appelant de décider
        languages,
        country,
        maxSources,
        selectedProviders,
        preferredSourceTypes: preferredTypes,
        useAI,
        enableGeographicLocalization,
        localGlobalRatio,
        
        // Données d'optimisation (optionnelles)
        semanticAnalysis,
        userPreferences,
        geographicContext: geographicAnalysis
      };

      // ✅ GARANTIE : Le backend met automatiquement à jour la collection "recherches"
      const result = await sourceService.getRecommendedSources(searchRequest, token);
      
      if (result.success && result.sources) {
        console.log(`✅ SynthesisService - ${result.sources.length} sources trouvées`);
        
        // ✅ Enrichissement automatique intelligent basé sur le contenu
        const enrichedSources = result.sources.map(source => {
          const enrichedSource = { ...source };
          
          // ✅ Calcul automatique du score d'inspiration basé sur le contenu
          enrichedSource.inspirationScore = this.calculateInspirationScore(source);
          
          return enrichedSource;
        });
        
        return {
          success: true,
          sources: enrichedSources,
          metadata: {
            ...result.metadata,
            sessionUpdated: true, // Confirme que la collection "recherches" est mise à jour
            searchMethod: 'unified'
          }
        };
      } else {
        return {
          success: false,
          error: 'Aucune source trouvée',
          sources: []
        };
      }
      
    } catch (error) {
      console.error('❌ SynthesisService - Erreur recherche unifiée:', error);
      return {
        success: false,
        error: error.message,
        sources: []
      };
    }
  }

  // ============================================================================
  // ✅ MÉTHODE EXISTANTE ENRICHIE : Sauvegarde de synthèse
  // WebSearchSpace continue à fonctionner, InspirationModule bénéficie des enrichissements
  // ============================================================================
  
  /**
   * ✅ SAUVEGARDE UNIFIÉE - Même fonction pour tous les modules
   * Auto-détection du type de contenu basé sur le titre et la description
   */
  async saveSynthesis(userId, competenceId, synthesisData, sources) {
    try {
      // 💾 DEBUG - Vérifier ce que le service reçoit
      console.log('💾 DEBUG - SynthesisService reçoit:', {
        searchObjective: synthesisData.searchObjective,
        query: synthesisData.query,
        title: synthesisData.title,
        userObjective: synthesisData.userObjective
      });
      
      console.log('🔍 Enrichissement des métadonnées des sources...');
      const enrichedSources = await this.enrichSourcesBatch(sources);
      
      const synthesisId = `synthesis_${Date.now()}`;
      const synthesisRef = doc(db, 'users', userId, 'competences', competenceId, 'syntheses', synthesisId);
      
      const sourcesHash = this.calculateSourcesHash(enrichedSources);
      const languageStats = this.analyzeLanguageDistribution(enrichedSources);
      const categoryStats = this.analyzeCategoryDistribution(enrichedSources);
      const contentTypeStats = this.analyzeContentTypeDistribution(enrichedSources);
      
      // ✅ Auto-détection du type de synthèse basé sur le contenu
      const title = synthesisData.title || "Synthèse de recherche";
      const content = synthesisData.extract || synthesisData.content || '';
      const isInspirationContent = this.detectInspirationContent(title, content);
      
      const synthesisDocument = {
        id: synthesisId,
        title: title,
        searchObjective: synthesisData.searchObjective || synthesisData.query || title,
        content: content,
        summary: synthesisData.summary || content.substring(0, 300) + (content.length > 300 ? '...' : ''),
        type: "synthesis",
        language: synthesisData.language || 'fr',
        
        // ✅ Auto-classification basée sur le contenu
        category: isInspirationContent ? 'inspiration' : 'general',
        isInspirational: isInspirationContent,
        
        // Métadonnées de recherche
        query: synthesisData.query || '',
        userObjective: synthesisData.userObjective || '',
        
        // Métadonnées de création
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        
        // Sources utilisées avec métadonnées enrichies
        sourcesUsed: enrichedSources.map(source => ({
          title: source.title,
          url: source.url || source.usableUrl,
          domain: source.domain,
          type: source.type,
          relevanceScore: source.relevanceScore,
          description: source.description,
          language: source.language,
          sourceCategory: source.sourceCategory,
          contentType: source.contentType,
          enrichedAt: source.enrichedAt,
          // ✅ Score d'inspiration calculé automatiquement pour toutes les sources
          inspirationScore: this.calculateInspirationScore(source),
          
          // ✅ NOUVEAU : Métadonnées du provider
          provider: source.provider || source.searchProvider || 'unknown',
          searchProvider: source.searchProvider || source.provider,
          
          // ✅ NOUVEAU : Indicateurs de qualité (si disponibles depuis le backend)
          reliabilityScore: source.reliabilityScore || null,
          popularityScore: source.popularityScore || null,
          trustScore: source.trustScore || null,
          authorityScore: source.authorityScore || null,
          
          // ✅ NOUVEAU : Métadonnées additionnelles du provider
          providerMetadata: source.providerMetadata || null,
          
          // ✅ NOUVEAU : Informations d'accessibilité
          accessibilityStatus: source.accessibilityStatus || null,
          verificationMethod: source.verificationMethod || null,
          hasUrlFallback: source.hasUrlFallback || false
        })),
        sourcesCount: enrichedSources.length,
        sourcesHash,
        
        // Statistiques enrichies
        languageDistribution: languageStats,
        categoryDistribution: categoryStats,
        contentTypeDistribution: contentTypeStats,
        
        // ✅ Métadonnées de service unifié
        serviceMetadata: {
          unifiedService: true,
          serviceVersion: '2.0-content-based',
          autoClassified: true
        },
        
        // Statut
        status: 'active',
        version: 1
      };
      
      await setDoc(synthesisRef, synthesisDocument);
      
      console.log('✅ Synthèse sauvegardée avec succès:', synthesisId);
      console.log(`📊 Type détecté: ${isInspirationContent ? 'Inspiration' : 'Général'}`);
      console.log('📊 Distribution des langues:', languageStats);
      console.log('📊 Distribution des catégories:', categoryStats);
      
      return { success: true, synthesisId, data: synthesisDocument };
      
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde de la synthèse:', error);
      return { success: false, error: error.message };
    }
  }

  // ============================================================================
  // ✅ NOUVELLE MÉTHODE : Chargement unifié des données existantes
  // ============================================================================
  
  /**
   * ✅ CHARGEMENT UNIFIÉ - Une seule méthode pour tous les modules
   */
  async loadExistingData(userId, competenceId, token) {
    try {
      console.log('🔄 SynthesisService - Chargement unifié des données');
      
      // 1. Récupération des sources via SourceService (collection "recherches")
      const sourcesResult = await sourceService.getSavedSources(userId, token, { competenceId });
      
      let formattedSources = [];
      if (sourcesResult && sourcesResult.success && sourcesResult.sources && sourcesResult.sources.length > 0) {
        const latestSession = sourcesResult.sources.sort((a, b) => 
          new Date(b.createdAt || b.updatedAt || 0) - new Date(a.createdAt || a.updatedAt || 0)
        )[0];
        
        if (latestSession.sources) {
          formattedSources = latestSession.sources.map(source => ({
            ...source,
            title: source.title || source.domain || 'Source sans titre',
            description: source.description || 'Aucune description disponible',
            url: source.url || `https://${source.domain}` || '#',
            domain: source.domain || 'Source externe',
            type: source.type || 'webpage',
            relevanceScore: source.relevanceScore || 5,
            sessionId: latestSession.id,
            // ✅ Score d'inspiration calculé automatiquement
            inspirationScore: this.calculateInspirationScore(source)
          }));
        }
      }

      // 2. Récupération de la synthèse
      const synthesisResult = await this.getLatestSynthesis(userId, competenceId);
      
      let transformedSynthesis = null;
      let resourceResults = [];
      
      if (synthesisResult.success && synthesisResult.synthesis) {
        transformedSynthesis = {
          title: synthesisResult.synthesis.title || "Synthèse de recherche",
          extract: synthesisResult.synthesis.content,
          content: synthesisResult.synthesis.content,
          url: "#",
          content_type: "synthesis",
          source: "Assistant IA",
          language: synthesisResult.synthesis.language || 'fr',
          type: "synthesis",
          category: synthesisResult.synthesis.category || 'general',
          datePublished: synthesisResult.synthesis.createdAt,
          relevanceScore: 100,
          id: synthesisResult.synthesis.id,
          sourcesCount: synthesisResult.synthesis.sourcesCount || 0,
          hasRealSynthesis: true,
          // ✅ Auto-détection du type inspirationnel
          isInspirational: synthesisResult.synthesis.isInspirational || false
        };
        
        // Sources de la synthèse ou fallback
        if (synthesisResult.synthesis.sourcesUsed && synthesisResult.synthesis.sourcesUsed.length > 0) {
          resourceResults = synthesisResult.synthesis.sourcesUsed.map(source => ({
            ...source,
            inspirationScore: source.inspirationScore || this.calculateInspirationScore(source)
          }));
        } else if (formattedSources.length > 0) {
          resourceResults = formattedSources;
        }
      }
      
      console.log(`✅ SynthesisService - Données chargées: ${formattedSources.length} sources, synthèse: ${transformedSynthesis ? 'Oui' : 'Non'}`);
      
      return {
        success: true,
        sources: formattedSources,
        synthesis: transformedSynthesis,
        resources: resourceResults,
        hasExistingData: formattedSources.length > 0 || !!transformedSynthesis
      };
      
    } catch (error) {
      console.error('❌ SynthesisService - Erreur chargement unifié:', error);
      return {
        success: false,
        error: error.message,
        sources: [],
        synthesis: null,
        resources: []
      };
    }
  }

  // ============================================================================
  // ✅ MÉTHODES UTILITAIRES INTELLIGENTES
  // ============================================================================
  
  /**
   * ✅ Calcul automatique du score d'inspiration basé sur le contenu
   */
  calculateInspirationScore(source) {
    let score = source.relevanceScore || 5;
    
    // Bonus selon le type de source
    if (source.type === 'Educational') score += 2;
    if (source.type === 'Non-profit') score += 1.5;
    if (source.type === 'Media') score += 1;
    
    // Bonus selon le domaine
    const inspiringDomains = ['ted.com', 'medium.com', 'linkedin.com', 'youtube.com', 'coursera.org', 'edx.org'];
    if (inspiringDomains.some(domain => source.domain?.includes(domain))) {
      score += 2;
    }
    
    // Bonus selon les mots-clés dans le titre/description
    const inspiringKeywords = ['inspiration', 'motivation', 'success', 'achievement', 'innovation', 'creative', 'leadership', 'growth', 'développement', 'réussite', 'innovation', 'créatif'];
    const content = `${source.title || ''} ${source.description || ''}`.toLowerCase();
    const keywordMatches = inspiringKeywords.filter(keyword => content.includes(keyword));
    score += keywordMatches.length * 0.5;
    
    return Math.min(10, Math.round(score));
  }
  
  /**
   * ✅ Détection automatique du contenu inspirationnel
   */
  detectInspirationContent(title, content) {
    const text = `${title} ${content}`.toLowerCase();
    
    // Mots-clés d'inspiration
    const inspirationKeywords = [
      'inspiration', 'inspiring', 'motivation', 'motivational', 'success', 'achievement', 
      'innovation', 'creative', 'creativity', 'leadership', 'growth', 'development',
      'inspirant', 'motivant', 'réussite', 'succès', 'innovation', 'créativité', 
      'leadership', 'développement', 'croissance', 'accomplissement'
    ];
    
    // Compter les occurrences
    const matches = inspirationKeywords.filter(keyword => text.includes(keyword));
    
    // Seuil : 2 mots-clés ou plus = contenu inspirationnel
    return matches.length >= 2;
  }

  // ============================================================================
  // MÉTHODES EXISTANTES INCHANGÉES
  // ============================================================================
  
  async getLatestSynthesis(userId, competenceId) {
    try {
      const synthesisCollectionRef = collection(db, 'users', userId, 'competences', competenceId, 'syntheses');
      const q = query(
        synthesisCollectionRef,
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { success: true, synthesis: null };
      }
      
      const docSnap = querySnapshot.docs[0];
      const synthesisData = { id: docSnap.id, ...docSnap.data() };
      
      if (synthesisData.createdAt) {
        synthesisData.createdAt = synthesisData.createdAt.toDate().toISOString();
      }
      if (synthesisData.updatedAt) {
        synthesisData.updatedAt = synthesisData.updatedAt.toDate().toISOString();
      }
      
      return { success: true, synthesis: synthesisData };
      
    } catch (error) {
      console.error('Erreur lors de la récupération de la synthèse:', error);
      return { success: false, error: error.message };
    }
  }
  
  calculateSourcesHash(sources) {
    if (!sources || sources.length === 0) return 'empty';
    
    const sourceString = sources
      .map(source => 
        `${source.url || source.usableUrl}_${source.title}_${source.relevanceScore}_${source.language}_${source.sourceCategory}_${source.contentType}`
      )
      .sort()
      .join('|');
    
    let hash = 0;
    for (let i = 0; i < sourceString.length; i++) {
      const char = sourceString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    return hash.toString();
  }

  analyzeLanguageDistribution(sources) {
    const languageCount = {};
    let totalSources = 0;
    
    sources.forEach(source => {
      const lang = source.language || 'unknown';
      languageCount[lang] = (languageCount[lang] || 0) + 1;
      totalSources++;
    });
    
    const distribution = {};
    Object.entries(languageCount).forEach(([lang, count]) => {
      distribution[lang] = {
        count: count,
        percentage: Math.round((count / totalSources) * 100)
      };
    });
    
    return distribution;
  }
  
  analyzeCategoryDistribution(sources) {
    const categoryCount = {};
    let totalSources = 0;
    
    sources.forEach(source => {
      const category = source.sourceCategory || 'Commercial';
      categoryCount[category] = (categoryCount[category] || 0) + 1;
      totalSources++;
    });
    
    const distribution = {};
    Object.entries(categoryCount).forEach(([category, count]) => {
      distribution[category] = {
        count: count,
        percentage: Math.round((count / totalSources) * 100)
      };
    });
    
    return distribution;
  }
  
  analyzeContentTypeDistribution(sources) {
    const typeCount = {};
    let totalSources = 0;
    
    sources.forEach(source => {
      const type = source.contentType || 'website';
      typeCount[type] = (typeCount[type] || 0) + 1;
      totalSources++;
    });
    
    const distribution = {};
    Object.entries(typeCount).forEach(([type, count]) => {
      distribution[type] = {
        count: count,
        percentage: Math.round((count / totalSources) * 100)
      };
    });
    
    return distribution;
  }

  async checkIfSourcesChanged(userId, competenceId, currentSources) {
    try {
      const latestSynthesis = await this.getLatestSynthesis(userId, competenceId);
      
      if (!latestSynthesis.success || !latestSynthesis.synthesis) {
        return { success: true, hasChanged: true, reason: 'Aucune synthèse existante' };
      }
      
      const currentHash = this.calculateSourcesHash(currentSources);
      const previousHash = latestSynthesis.synthesis.sourcesHash;
      
      const hasChanged = currentHash !== previousHash;
      
      return {
        success: true,
        hasChanged,
        reason: hasChanged ? 'Sources modifiées' : 'Sources identiques',
        previousCount: latestSynthesis.synthesis.sourcesCount,
        currentCount: currentSources.length,
        lastSynthesisDate: latestSynthesis.synthesis.createdAt
      };
      
    } catch (error) {
      console.error('Erreur lors de la vérification des sources:', error);
      return { success: false, error: error.message };
    }
  }

  async enrichSingleSource(source) {
    return await this.enrichSourceMetadata(source);
  }

  async reEnrichSources(sources) {
    console.log('Ré-enrichissement des sources existantes...');
    return await this.enrichSourcesBatch(sources);
  }

  getPerformanceStats() {
    return {
      lastEnrichmentTime: this.lastEnrichmentTime || null,
      totalSourcesEnriched: this.totalSourcesEnriched || 0,
      averageEnrichmentTime: this.averageEnrichmentTime || 0,
      cacheHitRate: ClientLanguageDetector.cache ? 
        `${ClientLanguageDetector.cache.size} entrées en cache` : 
        'Cache non disponible',
      serviceVersion: '2.0-unified-simple',
      contentBasedClassification: true
    };
  }
}

const synthesisService = new SynthesisService();
export default synthesisService;
